/**
 * security-tactical-catalog.ts
 *
 * Authoritative Security, AI Surveillance, Ballistic Defense & Tactical Tech Catalog.
 * 15 Specialized Products with technical specifications, MSRP, and categories.
 */

export interface SecurityProductDefinition {
  readonly sku: string;
  readonly productId: string;
  readonly name: string;
  readonly category: 'AI Surveillance' | 'Biometric Access' | 'Data Security' | 'Robotics & Patrol' | 'Ballistic Protection' | 'Executive Protection' | 'Elite Field Tech';
  readonly technicalSpec: string;
  readonly msrpUsd: number;
  readonly priceMinorUsd: string;
  readonly priceUgx: number;
  readonly imageUrl: string;
  readonly binLocation: string;
}

export const SECURITY_TACTICAL_CATALOG: readonly SecurityProductDefinition[] = [
  // ── 1. AI Surveillance ──────────────────────────────────────────────────
  {
    sku: 'SKU-UBQ-AIPRO',
    productId: 'prod-ubq-aipro',
    name: 'Ubiquiti Camera AI Professional',
    category: 'AI Surveillance',
    technicalSpec: 'High-end 4K (8MP) indoor/outdoor PoE camera with 3x optical zoom and advanced AI for long-distance detection of people and license plates.',
    msrpUsd: 499.00,
    priceMinorUsd: '49900',
    priceUgx: 1850000,
    imageUrl: './assets/products/ubiquiti_ai_pro.svg',
    binLocation: 'BIN-SURV-01',
  },
  {
    sku: 'SKU-REO-DUO3',
    productId: 'prod-reo-duo3',
    name: 'Reolink Duo 3 WiFi 16MP',
    category: 'AI Surveillance',
    technicalSpec: '16MP ultra-HD dual-lens camera providing a seamless 180° panoramic view with motion tracking and smart human/vehicle detection.',
    msrpUsd: 215.00,
    priceMinorUsd: '21500',
    priceUgx: 800000,
    imageUrl: './assets/products/reolink_duo3.svg',
    binLocation: 'BIN-SURV-02',
  },
  {
    sku: 'SKU-ANK-NCD800',
    productId: 'prod-ank-ncd800',
    name: 'ANNKE NCD800 4K Panoramic',
    category: 'AI Surveillance',
    technicalSpec: 'Dual-lens 4K PoE camera with NightChroma™ full-color night vision and precise AI human/vehicle detection to reduce false alerts.',
    msrpUsd: 239.98,
    priceMinorUsd: '23998',
    priceUgx: 890000,
    imageUrl: './assets/products/annke_ncd800.svg',
    binLocation: 'BIN-SURV-03',
  },

  // ── 2. Biometric Access & Data Security ─────────────────────────────────
  {
    sku: 'SKU-MKY-7IN1',
    productId: 'prod-mky-7in1',
    name: 'Makayuron 7-in-1 Smart Lock',
    category: 'Biometric Access',
    technicalSpec: 'Advanced biometric lock featuring 3D face recognition and palm vein scanning for high-security hands-free entry.',
    msrpUsd: 135.00,
    priceMinorUsd: '13500',
    priceUgx: 500000,
    imageUrl: './assets/products/makayuron_lock.svg',
    binLocation: 'BIN-SEC-01',
  },
  {
    sku: 'SKU-APR-FORT1TB',
    productId: 'prod-apr-fort1tb',
    name: 'Apricorn Aegis Padlock Fortress (1TB)',
    category: 'Data Security',
    technicalSpec: 'Hardware-encrypted USB 3.0 drive with an integrated keypad for PIN authentication and 256-bit AES XTS encryption.',
    msrpUsd: 217.99,
    priceMinorUsd: '21799',
    priceUgx: 810000,
    imageUrl: './assets/products/apricorn_padlock.svg',
    binLocation: 'BIN-SEC-02',
  },
  {
    sku: 'SKU-YUB-5CNFC',
    productId: 'prod-yub-5cnfc',
    name: 'Yubico YubiKey 5C NFC',
    category: 'Data Security',
    technicalSpec: 'Hardened security key providing phishing-resistant multi-factor authentication (MFA) for corporate accounts via USB-C and NFC.',
    msrpUsd: 55.00,
    priceMinorUsd: '5500',
    priceUgx: 205000,
    imageUrl: './assets/products/yubikey_5c.svg',
    binLocation: 'BIN-SEC-03',
  },

  // ── 3. Autonomous Robotics & Patrol ─────────────────────────────────────
  {
    sku: 'SKU-AST-BIZPATROL',
    productId: 'prod-ast-bizpatrol',
    name: 'Amazon Astro for Business',
    category: 'Robotics & Patrol',
    technicalSpec: 'Autonomous patrolling robot with night-vision periscope camera, AI human recognition, and 5,000 sq ft mapping capability.',
    msrpUsd: 2349.99,
    priceMinorUsd: '234999',
    priceUgx: 8700000,
    imageUrl: './assets/products/amazon_astro.svg',
    binLocation: 'BIN-ROBO-01',
  },

  // ── 4. Military-Grade Armor & Ballistic Protection ──────────────────────
  {
    sku: 'SKU-RMA-LVL4-1165',
    productId: 'prod-rma-lvl4-1165',
    name: 'RMA Level IV Multi-Curve Body Armor Plate (Model 1165)',
    category: 'Ballistic Protection',
    technicalSpec: 'Ultra-lightweight (5.3 lbs) ceramic body armor designed to stop armor-piercing rifle rounds (up to .30-06 M2 AP). Multi-curve ergonomic fit.',
    msrpUsd: 199.00,
    priceMinorUsd: '19900',
    priceUgx: 740000,
    imageUrl: './assets/products/rma_level4_plate.svg',
    binLocation: 'BIN-ARM-01',
  },
  {
    sku: 'SKU-LGF-LVL3-VSHLD',
    productId: 'prod-lgf-lvl3-vshld',
    name: 'Longfri Level III V-Shape Ballistic Shield',
    category: 'Ballistic Protection',
    technicalSpec: 'Tactical handheld ballistic barrier with integrated 1.75" thick bulletproof glass viewport. Rated for 7.62mm threats with V-shape deflection.',
    msrpUsd: 750.00,
    priceMinorUsd: '75000',
    priceUgx: 2780000,
    imageUrl: './assets/products/longfri_shield.svg',
    binLocation: 'BIN-ARM-02',
  },
  {
    sku: 'SKU-PBA-BP-IIIA',
    productId: 'prod-pba-bp-iiia',
    name: 'Soft Armor Backpack Insert (NIJ IIIA)',
    category: 'Ballistic Protection',
    technicalSpec: 'Flexible, lightweight soft-armor panel designed for backpack integration to stop common handgun rounds up to .44 Magnum.',
    msrpUsd: 125.00,
    priceMinorUsd: '12500',
    priceUgx: 465000,
    imageUrl: './assets/products/soft_armor_insert.svg',
    binLocation: 'BIN-ARM-03',
  },

  // ── 5. Executive Protection & Counter-Surveillance ──────────────────────
  {
    sku: 'SKU-PBA-EXEC-VEST',
    productId: 'prod-pba-exec-vest',
    name: 'Discreet Executive Kevlar Bulletproof Vest (Level IIIA)',
    category: 'Executive Protection',
    technicalSpec: 'Low-profile NIJ IIIA soft armor system for covert wear under formal attire and dress shirts, stopping handgun rounds up to .44 Magnum.',
    msrpUsd: 525.00,
    priceMinorUsd: '52500',
    priceUgx: 1950000,
    imageUrl: './assets/products/executive_vest.svg',
    binLocation: 'BIN-EXEC-01',
  },
  {
    sku: 'SKU-IMP-GHOST-3W',
    productId: 'prod-imp-ghost-3w',
    name: 'Impact Ghost 3-Wire Surveillance Earpiece Kit',
    category: 'Executive Protection',
    technicalSpec: 'Covert 3-wire communication kit with silent barrel push-to-talk (PTT) button and Kevlar-reinforced cabling for executive bodyguards.',
    msrpUsd: 137.28,
    priceMinorUsd: '13728',
    priceUgx: 510000,
    imageUrl: './assets/products/impact_ghost_earpiece.svg',
    binLocation: 'BIN-EXEC-02',
  },
  {
    sku: 'SKU-SPY-RF-SCAN3',
    productId: 'prod-spy-rf-scan3',
    name: 'Portable RF/GSM Signal Scanner & Bug Detector',
    category: 'Executive Protection',
    technicalSpec: 'Portable counter-surveillance tool scanning for hidden cameras, GSM listening bugs, and magnetic GPS trackers in rooms and vehicles.',
    msrpUsd: 395.00,
    priceMinorUsd: '39500',
    priceUgx: 1465000,
    imageUrl: './assets/products/rf_spy_detector.svg',
    binLocation: 'BIN-EXEC-03',
  },

  // ── 6. Elite Field & Tactical Tech ──────────────────────────────────────
  {
    sku: 'SKU-PVS14-GEN3-WP',
    productId: 'prod-pvs14-gen3-wp',
    name: 'AGM PVS-14 Gen 3 White Phosphor Night Vision Monocular',
    category: 'Elite Field Tech',
    technicalSpec: 'Military-standard Gen 3 White Phosphor night vision monocular for superior clarity, resolution, and depth perception in near-total darkness.',
    msrpUsd: 3350.00,
    priceMinorUsd: '335000',
    priceUgx: 12400000,
    imageUrl: './assets/products/pvs14_nvg.svg',
    binLocation: 'BIN-OPT-01',
  },
  {
    sku: 'SKU-ULF-PAD5-ULTRA',
    productId: 'prod-ulf-pad5-ultra',
    name: 'Ulefone Armor Pad 5 Ultra (MIL-STD-810H)',
    category: 'Elite Field Tech',
    technicalSpec: 'Ruggedized 11-inch tactical computing terminal (MIL-STD-810H) featuring thermal/night-vision sensors, 5G connectivity, and a 24,200mAh battery.',
    msrpUsd: 715.00,
    priceMinorUsd: '71500',
    priceUgx: 2650000,
    imageUrl: './assets/products/ulefone_pad5.svg',
    binLocation: 'BIN-TAC-01',
  },
];
